// src/router/systems.ts
import type { RouteRecordRaw } from "vue-router";
import type { RuntimeSystemConfig } from "@/types/system";
import { i18n } from "@/i18n";
import * as Sentry from "@sentry/vue";
import { useSystemStatusStore } from "@/stores/systemStatus";
import { systemConfigService } from "@/services/systemConfigService";

const t = i18n.global.t;

/**
 * 获取所有系统配置
 */
export function getSystems(): Record<string, RuntimeSystemConfig> {
  return systemConfigService.systems;
}

/**
 * 获取启用的系统列表
 */
export function getEnabledSystems(): RuntimeSystemConfig[] {
  return systemConfigService.enabledSystems;
}

// 检查系统是否可用（考虑配置启用状态和系统状态）
export function isSystemAvailable(systemName: string): boolean {
  // 首先检查系统是否在配置中启用
  const system = systemConfigService.getSystemConfig(systemName);
  if (!system?.enabled) {
    return false;
  }

  // 然后检查系统状态
  const statusStore = useSystemStatusStore();
  return statusStore.isSystemAvailable(systemName);
}

// 检查系统是否处于维护状态
export function isSystemInMaintenance(systemName: string): boolean {
  const statusStore = useSystemStatusStore();
  return statusStore.isSystemInMaintenance(systemName);
}

/**
 * 生成子系统路由
 */
export function generateSystemRoutes(): RouteRecordRaw[] {
  return getEnabledSystems().flatMap((system) => {
    const baseRoute = {
      path: system.path,
      children: [
        {
          path: "",
          name: `system-${system.name}`,
          component: () => import("@/views/authorize/AuthorizeView.vue"),
          meta: {
            title: t("authorize.title", {
              system: system.title,
            }),
            requiresAuth: true,
            system,
          },
        },
        {
          path: "login",
          name: `system-${system.name}-login`,
          component: () => import("@/views/login/LoginView.vue"),
          meta: {
            title: t("common.login", {
              system: system.title,
            }),
            public: true,
            system,
          },
        },
      ],
    };

    const legacyRoute = {
      path: `${system.path}/login.html`,
      name: `system-${system.name}-login-legacy`,
      component: () => import("@/views/login/LoginView.vue"),
      meta: {
        title: t("common.login", { system: system.title }),
        public: true,
        system,
      },
    };

    return [baseRoute, legacyRoute];
  });
}

// 默认的系统路由（初始为空，配置加载后会动态生成）
export const systemRoutes: RouteRecordRaw[] = [];

// 获取系统配置 - 增加对 .html 后缀的支持
export function getSystemConfig(path: string): RuntimeSystemConfig | undefined {
  return systemConfigService.getSystemByPath(path);
}

// 获取系统回调URL
export function getSystemCallback(
  system: RuntimeSystemConfig,
  customCallback?: string,
): string | undefined {
  if (customCallback) {
    try {
      // 验证自定义回调 URL 是否合法
      if (validateSystemUrl(customCallback, system.name)) {
        return customCallback;
      }
      console.warn(
        `Invalid custom callback URL for system ${system.name}:`,
        customCallback,
      );
      Sentry.captureMessage(
        `Invalid custom callback URL for system ${system.name}`,
        {
          level: "warning",
          tags: {
            component: "systems",
            action: "getSystemCallback",
            system: system.name,
          },
          extra: {
            customCallback,
            systemCallback: system.callback,
          },
        },
      );
    } catch (e) {
      console.warn(
        `Error validating custom callback URL for system ${system.name}:`,
        e,
      );
      Sentry.captureException(e, {
        tags: {
          component: "systems",
          action: "getSystemCallback",
          system: system.name,
        },
        extra: {
          customCallback,
          systemCallback: system.callback,
        },
      });
    }
  }

  // 如果没有自定义回调或自定义回调无效，使用系统默认回调
  const systemCallback = system.callback;
  if (!systemCallback) {
    console.warn(`No callback URL configured for system ${system.name}`);
    Sentry.captureMessage(
      `No callback URL configured for system ${system.name}`,
      {
        level: "warning",
        tags: {
          component: "systems",
          action: "getSystemCallback",
          system: system.name,
        },
      },
    );
    return undefined;
  }

  try {
    // 确保系统回调 URL 是完整的 URL
    const url = new URL(systemCallback);
    // 如果 URL 的路径部分与当前路径相同，说明配置有误
    if (url.pathname === import.meta.env.VITE_BASE_URL + system.path) {
      console.error(
        `System callback URL is same as current path for ${system.name}:`,
        systemCallback,
      );
      Sentry.captureMessage(`System callback URL is same as current path`, {
        level: "error",
        tags: {
          component: "systems",
          action: "getSystemCallback",
          system: system.name,
        },
        extra: {
          systemCallback,
          currentPath: system.path,
          baseUrl: import.meta.env.VITE_BASE_URL,
        },
      });
      return undefined;
    }
    return systemCallback;
  } catch (e) {
    console.error(
      `Invalid system callback URL for ${system.name}:`,
      systemCallback,
    );
    Sentry.captureException(e, {
      tags: {
        component: "systems",
        action: "getSystemCallback",
        system: system.name,
      },
      extra: {
        systemCallback,
      },
    });
    return undefined;
  }
}

export function validateSystemUrl(url: string, systemName?: string): boolean {
  try {
    const urlObj = new URL(url);

    // 获取所有启用系统的域名
    const enabledSystems = getEnabledSystems();
    const systemDomains = enabledSystems
      .filter((sys) => sys.callback)
      .map((sys) => {
        try {
          return new URL(sys.callback!).hostname;
        } catch (e) {
          console.warn(
            `Invalid callback URL for system ${sys.name}:`,
            sys.callback,
          );
          Sentry.captureException(e, {
            tags: {
              component: "systems",
              action: "validateSystemUrl",
              system: sys.name,
            },
            extra: {
              callback: sys.callback,
            },
          });
          return null;
        }
      })
      .filter((domain): domain is string => domain !== null);

    // 如果提供了特定系统名，只验证该系统的域名
    if (systemName) {
      const system = systemConfigService.getSystemConfig(systemName);
      if (system?.callback) {
        try {
          const systemDomain = new URL(system.callback).hostname;
          return urlObj.hostname === systemDomain;
        } catch (e) {
          console.warn(
            `Invalid callback URL for system ${systemName}:`,
            system.callback,
          );
          Sentry.captureException(e, {
            tags: {
              component: "systems",
              action: "validateSystemUrl",
              system: systemName,
            },
            extra: {
              callback: system.callback,
            },
          });
          return false;
        }
      }
    }

    // 验证URL是否属于任何已启用的系统
    return systemDomains.some((domain) => urlObj.hostname === domain);
  } catch (error) {
    console.warn("Invalid URL in validateSystemUrl:", url);
    Sentry.captureException(error, {
      tags: {
        component: "systems",
        action: "validateSystemUrl",
      },
      extra: {
        url,
        systemName,
      },
    });
    return false;
  }
}

/**
 * 初始化系统路由
 * 这个函数会在配置加载完成后被调用
 */
export function initializeSystemRoutes() {
  // 导入路由实例
  import("./index").then(({ default: router }) => {
    // 清空现有的系统路由
    systemRoutes.length = 0;

    // 生成新的系统路由
    const newRoutes = generateSystemRoutes();
    systemRoutes.push(...newRoutes);

    // 动态添加路由到路由实例
    newRoutes.forEach((route) => {
      router.addRoute(route);
    });

    console.log(`已初始化 ${newRoutes.length} 个系统路由`);
  });
}

/**
 * 监听配置变更并更新路由
 */
export function setupSystemConfigWatcher() {
  systemConfigService.onConfigChange((event) => {
    if (event.type === "systems") {
      initializeSystemRoutes();
      console.log("系统路由已更新:", event.changes);
    }
  });
}
